// FormatDetector - Handles detection of different audit data formats
export class FormatDetector {
  constructor() {
    this.detectionStats = {
      totalDetections: 0,
      cumulativeDetected: 0,
      legacyDetected: 0,
      unknownFormats: 0,
      detectionErrors: 0
    };
    
    this.formatSignatures = {
      cumulative: [
        'isChecked',
        'lastUpdated',
        'originalCheck',
        'modifiedAt',
        'transformedAt'
      ],
      legacy: [
        'isCompleted',
        'selectedPoints',
        'deductionPoints'
      ]
    };
  }

  // 🔥 ENHANCED: Better cumulative data format detection
  isCumulativeDataFormat(data) {
    this.detectionStats.totalDetections++;
    
    try {
      if (!data || typeof data !== 'object') {
        console.log('📊 Format detection: Invalid data type');
        this.detectionStats.unknownFormats++;
        return false;
      }
      
      const firstCategoryKey = Object.keys(data)[0];
      if (!firstCategoryKey) {
        console.log('📊 Format detection: No categories found');
        this.detectionStats.unknownFormats++;
        return false;
      }
      
      const firstCategory = data[firstCategoryKey];
      if (!firstCategory || !firstCategory.controlItems) {
        console.log('📊 Format detection: No control items found');
        this.detectionStats.unknownFormats++;
        return false;
      }
      
      const firstControlItemKey = Object.keys(firstCategory.controlItems)[0];
      if (!firstControlItemKey) {
        console.log('📊 Format detection: No control item keys found');
        this.detectionStats.unknownFormats++;
        return false;
      }
      
      const firstControlItem = firstCategory.controlItems[firstControlItemKey];
      if (!firstControlItem || !firstControlItem.checks) {
        console.log('📊 Format detection: No checks found');
        this.detectionStats.unknownFormats++;
        return false;
      }
      
      const firstCheckKey = Object.keys(firstControlItem.checks)[0];
      if (!firstCheckKey) {
        console.log('📊 Format detection: No check keys found');
        this.detectionStats.unknownFormats++;
        return false;
      }
      
      const firstCheck = firstControlItem.checks[firstCheckKey];
      
      // Enhanced detection: look for multiple cumulative format indicators
      const detectionResult = this.analyzeCheckStructure(firstCheck);
      
      console.log('📊 Format detection result:', {
        format: detectionResult.format,
        confidence: detectionResult.confidence,
        indicators: detectionResult.indicators,
        checkKeys: Object.keys(firstCheck || {})
      });
      
      const isCumulative = detectionResult.format === 'cumulative';
      
      if (isCumulative) {
        this.detectionStats.cumulativeDetected++;
      } else {
        this.detectionStats.legacyDetected++;
      }
      
      return isCumulative;
      
    } catch (error) {
      console.error('❌ Error in format detection:', error);
      this.detectionStats.detectionErrors++;
      return false;
    }
  }

  // Analyze check structure to determine format
  analyzeCheckStructure(check) {
    if (!check || typeof check !== 'object') {
      return {
        format: 'unknown',
        confidence: 0,
        indicators: [],
        reason: 'Invalid check object'
      };
    }
    
    const checkKeys = Object.keys(check);
    const cumulativeScore = this.calculateFormatScore(checkKeys, this.formatSignatures.cumulative);
    const legacyScore = this.calculateFormatScore(checkKeys, this.formatSignatures.legacy);
    
    // Determine format based on scores
    let format, confidence;
    const indicators = [];
    
    if (cumulativeScore > legacyScore && cumulativeScore > 0) {
      format = 'cumulative';
      confidence = Math.min(100, (cumulativeScore / this.formatSignatures.cumulative.length) * 100);
      indicators.push(...this.formatSignatures.cumulative.filter(sig => checkKeys.includes(sig)));
    } else if (legacyScore > 0) {
      format = 'legacy';
      confidence = Math.min(100, (legacyScore / this.formatSignatures.legacy.length) * 100);
      indicators.push(...this.formatSignatures.legacy.filter(sig => checkKeys.includes(sig)));
    } else {
      format = 'unknown';
      confidence = 0;
    }
    
    return {
      format,
      confidence: Math.round(confidence),
      indicators,
      scores: {
        cumulative: cumulativeScore,
        legacy: legacyScore
      },
      totalKeys: checkKeys.length,
      availableKeys: checkKeys
    };
  }

  // Calculate format score based on signature matching
  calculateFormatScore(checkKeys, signature) {
    let score = 0;
    let weightedScore = 0;
    
    signature.forEach((signatureKey, index) => {
      if (checkKeys.includes(signatureKey)) {
        score++;
        // Give higher weight to earlier (more important) signatures
        const weight = signature.length - index;
        weightedScore += weight;
      }
    });
    
    return weightedScore;
  }

  // Detect format with detailed analysis
  detectFormatWithAnalysis(data) {
    const analysis = {
      timestamp: new Date().toISOString(),
      dataStructure: this.analyzeDataStructure(data),
      formatDetection: null,
      recommendations: []
    };
    
    if (!data || typeof data !== 'object') {
      analysis.formatDetection = {
        format: 'invalid',
        confidence: 0,
        reason: 'Data is not an object'
      };
      
      analysis.recommendations.push({
        type: 'error',
        message: 'Provide valid data object for format detection'
      });
      
      return analysis;
    }
    
    // Analyze multiple checks for better confidence
    const checkAnalyses = [];
    let checksAnalyzed = 0;
    const maxChecksToAnalyze = 5; // Analyze up to 5 checks for better confidence
    
    outerLoop: for (const categoryId of Object.keys(data)) {
      const categoryData = data[categoryId];
      
      if (categoryData?.controlItems) {
        for (const controlItemId of Object.keys(categoryData.controlItems)) {
          const controlItemData = categoryData.controlItems[controlItemId];
          
          if (controlItemData?.checks) {
            for (const checkId of Object.keys(controlItemData.checks)) {
              const checkData = controlItemData.checks[checkId];
              
              const checkAnalysis = this.analyzeCheckStructure(checkData);
              checkAnalyses.push({
                categoryId,
                controlItemId,
                checkId,
                analysis: checkAnalysis
              });
              
              checksAnalyzed++;
              if (checksAnalyzed >= maxChecksToAnalyze) break outerLoop;
            }
          }
        }
      }
    }
    
    // Aggregate results from multiple checks
    if (checkAnalyses.length > 0) {
      const formatCounts = { cumulative: 0, legacy: 0, unknown: 0 };
      let totalConfidence = 0;
      
      checkAnalyses.forEach(({ analysis: checkAnalysis }) => {
        formatCounts[checkAnalysis.format]++;
        totalConfidence += checkAnalysis.confidence;
      });
      
      const averageConfidence = totalConfidence / checkAnalyses.length;
      const dominantFormat = Object.keys(formatCounts).reduce((a, b) => 
        formatCounts[a] > formatCounts[b] ? a : b
      );
      
      analysis.formatDetection = {
        format: dominantFormat,
        confidence: Math.round(averageConfidence),
        checksAnalyzed,
        formatDistribution: formatCounts,
        detailedAnalyses: checkAnalyses
      };
      
      // Generate recommendations based on results
      if (dominantFormat === 'unknown') {
        analysis.recommendations.push({
          type: 'warning',
          message: 'Unable to determine data format. Check data structure.',
          suggestion: 'Ensure data follows expected audit format patterns'
        });
      } else if (averageConfidence < 70) {
        analysis.recommendations.push({
          type: 'warning',
          message: `Low confidence in format detection (${Math.round(averageConfidence)}%)`,
          suggestion: 'Verify data integrity and completeness'
        });
      } else {
        analysis.recommendations.push({
          type: 'success',
          message: `Format detected as ${dominantFormat} with ${Math.round(averageConfidence)}% confidence`
        });
      }
      
    } else {
      analysis.formatDetection = {
        format: 'empty',
        confidence: 0,
        reason: 'No checks found in data'
      };
      
      analysis.recommendations.push({
        type: 'error',
        message: 'No audit checks found in the provided data'
      });
    }
    
    return analysis;
  }

  // Analyze overall data structure
  analyzeDataStructure(data) {
    const structure = {
      totalCategories: 0,
      totalControlItems: 0,
      totalChecks: 0,
      hasValidStructure: false,
      structureIssues: []
    };
    
    if (!data || typeof data !== 'object') {
      structure.structureIssues.push('Data is not an object');
      return structure;
    }
    
    structure.totalCategories = Object.keys(data).length;
    structure.hasValidStructure = structure.totalCategories > 0;
    
    Object.keys(data).forEach(categoryId => {
      const categoryData = data[categoryId];
      
      if (!categoryData || typeof categoryData !== 'object') {
        structure.structureIssues.push(`Category ${categoryId} is not an object`);
        return;
      }
      
      if (!categoryData.controlItems) {
        structure.structureIssues.push(`Category ${categoryId} missing controlItems`);
        return;
      }
      
      const controlItemKeys = Object.keys(categoryData.controlItems);
      structure.totalControlItems += controlItemKeys.length;
      
      controlItemKeys.forEach(controlItemId => {
        const controlItemData = categoryData.controlItems[controlItemId];
        
        if (!controlItemData || typeof controlItemData !== 'object') {
          structure.structureIssues.push(`Control item ${controlItemId} in category ${categoryId} is not an object`);
          return;
        }
        
        if (!controlItemData.checks) {
          structure.structureIssues.push(`Control item ${controlItemId} in category ${categoryId} missing checks`);
          return;
        }
        
        structure.totalChecks += Object.keys(controlItemData.checks).length;
      });
    });
    
    return structure;
  }

  // Batch format detection for multiple data sets
  batchDetectFormat(dataSets) {
    console.log(`🔄 Starting batch format detection for ${dataSets.length} data sets...`);
    
    const results = [];
    const startTime = Date.now();
    
    dataSets.forEach((data, index) => {
      try {
        const detection = this.detectFormatWithAnalysis(data);
        
        results.push({
          index,
          success: true,
          format: detection.formatDetection.format,
          confidence: detection.formatDetection.confidence,
          checksAnalyzed: detection.formatDetection.checksAnalyzed,
          recommendations: detection.recommendations
        });
        
      } catch (error) {
        results.push({
          index,
          success: false,
          error: error.message
        });
      }
    });
    
    const duration = Date.now() - startTime;
    const successCount = results.filter(r => r.success).length;
    
    // Analyze batch results
    const formatCounts = { cumulative: 0, legacy: 0, unknown: 0, invalid: 0, empty: 0 };
    results.filter(r => r.success).forEach(result => {
      formatCounts[result.format] = (formatCounts[result.format] || 0) + 1;
    });
    
    console.log(`✅ Batch format detection completed: ${successCount}/${dataSets.length} successful in ${duration}ms`);
    
    return {
      results,
      summary: {
        totalDataSets: dataSets.length,
        successfulDetections: successCount,
        failedDetections: dataSets.length - successCount,
        formatDistribution: formatCounts,
        totalDuration: duration,
        averageDuration: duration / dataSets.length
      }
    };
  }

  // Register custom format signature
  registerFormatSignature(formatName, signature) {
    if (!Array.isArray(signature)) {
      throw new Error('Format signature must be an array of strings');
    }
    
    this.formatSignatures[formatName] = signature;
    console.log(`✅ Registered custom format signature: ${formatName}`);
  }

  // Get format detection statistics
  getStats() {
    const total = this.detectionStats.totalDetections;
    
    return {
      ...this.detectionStats,
      accuracy: total > 0 ? 
        ((total - this.detectionStats.unknownFormats - this.detectionStats.detectionErrors) / total * 100).toFixed(2) + '%' : '0%',
      cumulativeRate: total > 0 ? (this.detectionStats.cumulativeDetected / total * 100).toFixed(2) + '%' : '0%',
      legacyRate: total > 0 ? (this.detectionStats.legacyDetected / total * 100).toFixed(2) + '%' : '0%',
      errorRate: total > 0 ? (this.detectionStats.detectionErrors / total * 100).toFixed(2) + '%' : '0%'
    };
  }

  // Reset detection statistics
  resetStats() {
    this.detectionStats = {
      totalDetections: 0,
      cumulativeDetected: 0,
      legacyDetected: 0,
      unknownFormats: 0,
      detectionErrors: 0
    };
    console.log('📊 Format detection statistics reset');
  }

  // Export detector data
  export() {
    return {
      exportedAt: new Date().toISOString(),
      stats: this.getStats(),
      formatSignatures: { ...this.formatSignatures }
    };
  }

  // Validate format signature
  validateFormatSignature(data, expectedFormat) {
    const detectionResult = this.detectFormatWithAnalysis(data);
    
    return {
      isValid: detectionResult.formatDetection.format === expectedFormat,
      detectedFormat: detectionResult.formatDetection.format,
      expectedFormat,
      confidence: detectionResult.formatDetection.confidence,
      match: detectionResult.formatDetection.format === expectedFormat,
      analysis: detectionResult
    };
  }

  // Get format recommendations
  getFormatRecommendations(data) {
    const analysis = this.detectFormatWithAnalysis(data);
    const recommendations = [...analysis.recommendations];
    
    // Add performance recommendations
    if (analysis.dataStructure.totalChecks > 1000) {
      recommendations.push({
        type: 'performance',
        message: 'Large dataset detected. Consider using batch processing.',
        suggestion: 'Use batchDetectFormat() for better performance with large datasets'
      });
    }
    
    if (analysis.formatDetection.confidence < 50) {
      recommendations.push({
        type: 'quality',
        message: 'Low format detection confidence suggests data quality issues.',
        suggestion: 'Review data structure and ensure consistency across all checks'
      });
    }
    
    return recommendations;
  }

  // Reset detector
  reset() {
    this.resetStats();
    console.log('🔄 Format detector reset complete');
  }
}